import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Smartphone, Globe, Zap } from "lucide-react";
import CancelStrategy from "@/components/CancelStrategy";

const HOW = {
  apple: {
    title: "Apple Subscriptions",
    steps: [
      "We'll deep-link you into Settings → Apple ID → Subscriptions.",
      "Tap the subscription and 'Cancel Subscription' — done.",
    ],
    icon: Smartphone,
  },
  google: {
    title: "Google Play Subscriptions",
    steps: [
      "Opens Google Play → Subscriptions.",
      "Tap the subscription and Cancel.",
    ],
    icon: Smartphone,
  },
  web: {
    title: "Web merchant",
    steps: [
      "Opens the merchant's direct cancel URL — no digging through settings.",
      "Follow the flow on the merchant's page.",
    ],
    icon: Globe,
  },
  tiktok_shop: {
    title: "TikTok Shop",
    steps: [
      "Opens TikTok Shop → Account → Subscription.",
      "Tap Cancel Subscription and confirm.",
    ],
    icon: Globe,
  },
  short_drama: {
    title: "Short-drama app",
    steps: [
      "These apps hide the cancel deep inside the settings.",
      "We take you straight to the account page.",
    ],
    icon: Globe,
  },
};

export default function CancelModal({ leak, onClose, onConfirm }) {
  const [strategy, setStrategy] = useState(leak.cancel_strategy || null);
  const info = HOW[leak.platform] || HOW.web;
  const Icon = info.icon;
  const noDeepLink = strategy && ["email", "chat", "phone", "bank"].includes(strategy.best_path);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#141618] border-[#282C30] text-[#F4F1EA] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cancel-modal">
        <DialogHeader>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">PLATFORM-CORRECT CANCEL</div>
          <DialogTitle className="font-display text-2xl tracking-tight">Cancel {leak.merchant}</DialogTitle>
          <DialogDescription className="text-[#A0A7B0]">
            {info.title} · ${leak.amount.toFixed(2)}/{leak.interval}
          </DialogDescription>
        </DialogHeader>
        <CancelStrategy leak={leak} onStrategy={setStrategy} />
        {!strategy && (
          <div className="mt-2 p-4 rounded-md bg-[#0C0D0E] border border-[#282C30]">
            <div className="flex items-start gap-3">
              <Icon className="h-5 w-5 text-[#00E676] shrink-0 mt-0.5" />
              <div className="space-y-2">
                {info.steps.map((s, i) => (
                  <div key={s} className="text-sm text-[#A0A7B0] flex gap-2">
                    <span className="font-mono text-[10px] mt-1 text-[#606872]">{String(i + 1).padStart(2, "0")}</span>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-2 break-all">
          → {leak.cancel_url}
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="btn-outline-paper flex-1" data-testid="cancel-modal-close">Not now</Button>
          <Button className="btn-emerald flex-1" onClick={() => onConfirm(strategy)} data-testid="cancel-modal-confirm">
            <Zap className="h-4 w-4 mr-1" /> {noDeepLink ? "Mark cancelled" : "Open cancel page"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
